import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import './TruncatedComment.css';

/**
 * TruncatedComment displays comments with a fixed character length and uniform width.
 * Every comment preview spans the exact same length.
 * Hovering reveals the complete text in a floating portal popover.
 */
const TruncatedComment = ({ text, maxLength = 30, className = '' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placeAbove: false });
  const triggerRef = useRef(null);

  if (!text || typeof text !== 'string' || !text.trim()) {
    return <span className="text-muted">—</span>;
  }

  const cleanText = text.trim();
  const isTruncated = cleanText.length > maxLength;
  const visibleText = isTruncated ? `${cleanText.slice(0, maxLength)}...` : cleanText;

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const placeAbove = window.innerHeight - rect.bottom < 130;
      setCoords({
        top: placeAbove ? rect.top - 8 : rect.bottom + 8,
        left: Math.max(12, Math.min(rect.left, window.innerWidth - 380)),
        placeAbove,
      });
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      ref={triggerRef}
      className={`truncated-comment-container ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="truncated-comment-trigger">
        {visibleText}
      </span>

      {/* Render full text popover in portal so it never clips */}
      {isHovered && isTruncated && ReactDOM.createPortal(
        <div
          className={`truncated-comment-popover ${coords.placeAbove ? 'place-above' : 'place-below'}`}
          style={{
            position: 'fixed',
            top: coords.placeAbove ? 'auto' : `${coords.top}px`,
            bottom: coords.placeAbove ? `${window.innerHeight - coords.top}px` : 'auto',
            left: `${coords.left}px`,
            zIndex: 999999,
          }}
          role="tooltip"
        >
          <div className="truncated-comment-popover-content">{cleanText}</div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TruncatedComment;
